import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Sparkles, Loader2, RefreshCw, User, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AI-generated executive & advisory commentary powered by Claude Sonnet 4.5.
 * Only meaningful for windows with ≥2 months (needs MoM movement to narrate).
 *
 * Props:
 *  - companyContext: {companyId, branchId}
 *  - filters: {from_date, to_date}
 *  - compact: if true, renders a small teaser (executive-only, first paragraph)
 *  - onOpenFull: optional callback to switch to the full AI tab (used by teaser)
 */
export function AICommentary({ companyContext, filters, compact, onOpenFull }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const companyId = companyContext?.companyId;
  const branchId = companyContext?.branchId;
  const monthsCount = filters?.from_date && filters?.to_date
    ? monthDiff(filters.from_date, filters.to_date)
    : 1;

  const fetchCommentary = useCallback(
    async (forceRefresh = false) => {
      if (monthsCount < 2) {
        setData(null);
        setLoading(false);
        return;
      }
      forceRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      try {
        const params = { ...filters };
        if (companyId) params.company_id = companyId;
        if (branchId) params.branch_id = branchId;
        if (forceRefresh) params.force_refresh = true;
        const res = await axios.get(`${API}/mis/commentary/ai`, { params, timeout: 120000 });
        setData(res.data);
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Failed to generate commentary";
        setError(msg);
        if (forceRefresh) toast.error("Failed to refresh AI commentary");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters, companyId, branchId, monthsCount],
  );

  useEffect(() => {
    fetchCommentary(false);
  }, [fetchCommentary]);

  // Not enough months for MoM analysis
  if (monthsCount < 2) {
    if (compact) return null;
    return (
      <div
        data-testid="ai-commentary-need-multi-month"
        className="bg-zinc-50 border border-zinc-200 rounded-lg p-8 text-center"
      >
        <Sparkles className="w-6 h-6 mx-auto text-zinc-400 mb-2" />
        <p className="text-sm text-zinc-600 font-medium">AI Insights need ≥ 2 months of data</p>
        <p className="text-xs text-zinc-400 mt-1">
          Widen the date filter to include at least two months for MoM narrative.
        </p>
      </div>
    );
  }

  // -------- Compact teaser (Summary tab) --------
  if (compact) {
    const previewText = data?.executive
      ? data.executive.split(/\n\n+/)[0]
      : loading
      ? "Generating AI insights…"
      : error
      ? "AI insights unavailable."
      : "AI insights not available yet.";
    return (
      <div
        data-testid="ai-commentary-teaser"
        className="rounded-lg border border-zinc-950/10 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-800 text-zinc-100 p-5"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-400">
                AI Insights · Executive Snapshot
              </span>
              {data?.cached && (
                <span className="text-[9px] font-mono text-zinc-500">cached</span>
              )}
            </div>
            <p className="text-[13px] leading-relaxed text-zinc-200 line-clamp-4">
              {loading ? (
                <span className="inline-flex items-center gap-2 text-zinc-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating executive summary…
                </span>
              ) : (
                previewText
              )}
            </p>
          </div>
          {onOpenFull && !loading && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenFull}
              data-testid="ai-commentary-open-full"
              className="text-emerald-400 hover:text-emerald-300 hover:bg-zinc-800 text-[11px] font-semibold whitespace-nowrap"
            >
              Full insights →
            </Button>
          )}
        </div>
      </div>
    );
  }

  // -------- Full tab (Executive + Advisory) --------
  return (
    <div className="space-y-4" data-testid="ai-commentary-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-600" />
          <h2 className="text-lg font-bold text-zinc-950" style={{ fontFamily: "'Chivo'" }}>
            AI Insights
          </h2>
          <span className="text-[10px] font-mono text-zinc-400">
            {data?.model || "claude-sonnet-4-5"}
          </span>
          {data?.cached && (
            <span className="text-[10px] font-mono text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">
              cached
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchCommentary(true)}
          disabled={loading || refreshing}
          data-testid="ai-commentary-refresh"
          className="text-[11px] font-semibold"
        >
          {refreshing ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          )}
          Regenerate
        </Button>
      </div>

      {loading && (
        <div
          className="flex items-center gap-3 py-16 justify-center text-zinc-500"
          data-testid="ai-commentary-loading"
        >
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Claude is analysing your MIS…</span>
        </div>
      )}

      {!loading && error && (
        <div
          data-testid="ai-commentary-error"
          className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ai-commentary-cards">
          <NarrativeCard
            icon={User}
            title="Executive (CFO)"
            subtitle="Numbers-first MoM narrative"
            body={data.executive}
            accent="text-emerald-700"
            border="border-emerald-100"
            bg="bg-emerald-50/40"
            testid="ai-commentary-executive"
          />
          <NarrativeCard
            icon={Briefcase}
            title="Advisory (Consultant)"
            subtitle="Hypotheses & recommended actions"
            body={data.advisory}
            accent="text-indigo-700"
            border="border-indigo-100"
            bg="bg-indigo-50/40"
            testid="ai-commentary-advisory"
          />
        </div>
      )}

      {!loading && data && (
        <p className="text-[10px] font-mono text-zinc-400 text-right">
          Generated from {data.months_available || 0} monthly data points ·{" "}
          {(data.months_covered || []).join(" → ")}
        </p>
      )}
    </div>
  );
}

function NarrativeCard({ icon: Icon, title, subtitle, body, accent, border, bg, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg ${border} ${bg} border p-5 space-y-3`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${accent}`} />
        <div>
          <div className={`text-[13px] font-bold ${accent}`} style={{ fontFamily: "'Chivo'" }}>
            {title}
          </div>
          <div className="text-[10px] text-zinc-500">{subtitle}</div>
        </div>
      </div>
      <div className="text-[13px] leading-relaxed text-zinc-800 whitespace-pre-line">
        {body || <span className="text-zinc-400 italic">No narrative returned.</span>}
      </div>
    </div>
  );
}

function monthDiff(fromDate, toDate) {
  try {
    const [fy, fm] = fromDate.split("-").map(Number);
    const [ty, tm] = toDate.split("-").map(Number);
    return (ty - fy) * 12 + (tm - fm) + 1;
  } catch {
    return 1;
  }
}

export default AICommentary;
